import type { HeaderProps as SharedHeaderProps } from '@latitude/header';
import { Header as SharedHeader } from '@latitude/header';
import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import type { ProgressBarProps } from '@latitude/progress-bar';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import { Description, Title } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import type { FC, ReactNode } from 'react';

const modalTitle = (title: ReactNode) => (
  <Title asChild>
    <div>{title}</div>
  </Title>
);

// Radix still needs a Title in the tree for aria-labelledby when the modal has
// no visible title, so it renders visually hidden rather than not at all.
const hiddenModalTitle = (title: ReactNode) => (
  <VisuallyHidden>{modalTitle(title)}</VisuallyHidden>
);

/**
 * Represents the props for the Header component.
 */
export type HeaderProps = {
  /**
   * The title of the header.
   */
  title: string;

  /**
   * The subtitle of the header (optional).
   */
  subTitle?: ReactNode;

  /**
   * Callback function to be called when the header is closed.
   */
  onClose?: () => void;

  leadingElement?: ReactNode;

  /**
   * If hidden, ensure an alternative way exists to close the the modal.
   * @default false
   */
  hideCloseButton?: boolean;

  /**
   * When true, truncates a long title to one line with an ellipsis and
   * shows the full title in a tooltip on hover, instead of wrapping.
   * @default false
   */
  truncateTitle?: boolean;

  /**
   * Renders a 4px progress bar on the header's bottom edge, in place of the
   * separator. Used by multi-step surfaces such as `@latitude/form-wizard`.
   */
  progress?: Omit<ProgressBarProps, 'size'>;

  /**
   * Props forwarded straight to the shared `@latitude/header`. Merged over the
   * flat props above, so a call site can adopt it one field at a time.
   *
   * `titleWrapper` and `actions` are not accepted: Modal owns the first for
   * Radix's `aria-labelledby` and the second for the close button, and letting
   * a caller replace either would silently break labelling or remove the only
   * way out of the dialog.
   *
   * `Partial` because the shared type requires `title`, which Modal's own flat
   * prop already supplies. Without it, adopting a single field would mean
   * repeating the title.
   */
  headerProps?: Partial<Omit<SharedHeaderProps, 'titleWrapper' | 'actions'>>;
};

/**
 * Header component of the modal. A thin adapter over the shared
 * `@latitude/header`, mapping the modal's props onto it and supplying the
 * Radix labelling elements that package deliberately does not know about.
 */
export const Header: FC<HeaderProps> = ({
  title,
  subTitle,
  onClose,
  leadingElement,
  hideCloseButton = false,
  truncateTitle = false,
  progress,
  headerProps,
}) => {
  const description = (
    <Text
      variant="bodyRegular"
      color="muted"
      data-testid="modal-header-sub-title"
      asChild
    >
      <Description asChild>
        <div>{subTitle || ' '}</div>
      </Description>
    </Text>
  );

  return (
    <>
      <SharedHeader
        data-testid="modal-header"
        // A blank title still renders, hidden, so Radix keeps its labelling.
        title={title || ' '}
        titleWrapper={title ? modalTitle : hiddenModalTitle}
        truncateTitle={truncateTitle}
        progress={progress}
        leadingElement={leadingElement}
        subtext={subTitle ? description : undefined}
        {...headerProps}
        // After the spread, and merged rather than replaced: the pin is what
        // stops a modal that toggles its close button from changing height
        // mid-flow, so a caller's className adds to it instead of dropping it.
        className={cn(
          'min-h-sizing-sys-size-64 flex-none',
          headerProps?.className,
        )}
        actions={
          hideCloseButton ? undefined : (
            <IconButton
              variant="strong"
              type="ghost"
              size="small"
              data-testid="modal-close-button"
              onClick={onClose}
              aria-label="Close"
              icon={<Icon name="x-close" />}
            />
          )
        }
      />
      {/* Radix wants a Description for aria-describedby even with no subtitle.
          It sits outside the header rather than in its subtext slot, which
          would otherwise add the title block's 4px gap and make it 60px. */}
      {!subTitle && <VisuallyHidden>{description}</VisuallyHidden>}
    </>
  );
};
